import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Check, ChevronRight, Camera, Send, MessageSquare, Plus, Ban } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { scrollPageTop } from "@/lib/scroll";
import { S } from "./strings";
import { Shell, Head, BackLink, Progress, Card, AreaRow, Tick, PrimaryButton, QuestionLine, Filed, PhotoStrip, PhotoPrompt, VisitRecap, ThreadMsg, PinnedNotes } from "./parts";
import { OfficeLists, AssignSheet, StepOffSheet } from "./handoff";

// "Sunday, October 18" reads like a date a person wrote; "2026-10-18" reads like a database.
const pretty = (iso, lang, short = false) => {
  const [y, m, d] = String(iso || "").split("-").map(Number);
  if (!y) return iso || "";
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(lang === "es" ? "es-ES" : "en-US",
    short ? { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" }
      : { weekday: "long", month: "long", day: "numeric", timeZone: "UTC" });
};

// "The office asked about Hose down the trash room floor" beats a bare "the office asked".
const questionLabel = (visit, t) => {
  const last = [...(visit.thread || [])].reverse().find((e) => e.side === "manager" && !["system", "note"].includes(e.kind));
  const task = last?.task_id && visit.tasks.find((x) => x.id === last.task_id);
  return task ? `${t.officeAsked} ${task.label}` : t.officeAskedPlain;
};

const STATUS_WORD = {
  approved: "stApproved", submitted: "stWaiting", vendor_replied: "stWaiting",
  needs_attention: "stRedo", excused: "stExcused", missed: "missed", scheduled: "stScheduled",
};

const TaskCard = ({ t, task, busy, onTick, onPhoto, onSkip, onStep, onNote, lang }) => {
  const needsPhoto = task.photo_required && !task.photos.length && !task.skipped;
  const steps = task.steps || [];
  return (
    <div data-testid={`work-task-${task.id}`}
      className={`mb-2.5 rounded-2xl border px-4 py-3.5 ${
        task.done ? "border-emerald-200 bg-emerald-50/70"
          : needsPhoto ? "border-amber-300 bg-amber-50"
            : task.skipped ? "border-sand-200 bg-sand-100/60" : "border-sand-200 bg-white"}`}>
      <div className="flex items-start gap-3">
        <Tick on={task.done} testid={`work-tick-${task.id}`} onClick={() => !busy && !steps.length && onTick(task)} />
        <div className="min-w-0 flex-1">
          <div className={`text-[15px] font-semibold leading-snug ${task.done ? "text-emerald-800" : "text-ink"}`}>{task.label}</div>
          {task.added_by && <div className="mt-0.5 text-[11.5px] text-ink/40">{t.addedBy} {task.added_by}</div>}
          {task.carried_from && (
            <div className="mt-0.5 text-[11.5px] font-semibold text-amber-700" data-testid={`work-task-carried-${task.id}`}>
              {t.carriedFrom(pretty(task.carried_from.date, lang, true))}
            </div>
          )}
          {task.done && task.at && <div className="mt-0.5 text-[11.5px] text-ink/40">{task.at}</div>}
          {!task.done && task.hint && <div className="mt-0.5 text-[11.5px] text-amber-800">{task.hint}</div>}
          {task.skipped && <div className="mt-1 text-[11.5px] text-ink/50">{t.told}: {task.reason}</div>}
          {steps.length > 0 && (
            <div className="mt-2 space-y-1" data-testid={`work-steps-${task.id}`}>
              {steps.map((s) => (
                <button key={s.id} data-testid={`work-step-${s.id}`} disabled={busy}
                  onClick={() => onStep(task, s)}
                  className="flex w-full items-center gap-2.5 rounded-xl px-1 py-1 text-left active:bg-sand-100">
                  <span className={`grid h-5 w-5 shrink-0 place-items-center rounded-full border-2 ${
                    s.done ? "border-emerald-700 bg-emerald-700" : "border-sand-300 bg-white"}`}>
                    {s.done && <Check className="h-3 w-3 text-white" strokeWidth={3.5} />}
                  </span>
                  <span className={`text-[13.5px] ${s.done ? "text-emerald-800 line-through decoration-emerald-700/40" : "text-ink-700"}`}>{s.label}</span>
                </button>
              ))}
            </div>
          )}
          {task.note && (
            <div className="mt-2 rounded-xl bg-sand-100/80 px-3 py-2 text-[12.5px] text-ink-700" data-testid={`work-task-note-${task.id}`}>
              {task.note}
            </div>
          )}
          {task.photos.length > 0 && <PhotoStrip photos={task.photos} onAdd={() => onPhoto(task)} addLabel={t.addMore} />}
          {needsPhoto && <PhotoPrompt t={t} testid={`work-photo-${task.id}`} onPick={() => onPhoto(task)} />}
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5">
            {/* ONE way to add a photo, never two: the big prompt when it's required, a "+" tile
                next to the ones already taken, and this link only when neither applies. */}
            {!needsPhoto && !task.photos.length && (
              <button onClick={() => onPhoto(task)} data-testid={`work-addphoto-${task.id}`}
                className="inline-flex items-center gap-1 text-[12px] font-medium text-ink/45 underline underline-offset-2">
                <Camera className="h-3.5 w-3.5" /> {t.addPhoto}
              </button>
            )}
            <button onClick={() => onNote(task)} data-testid={`work-addnote-${task.id}`}
              className="inline-flex items-center gap-1 text-[12px] font-medium text-ink/45 underline underline-offset-2">
              <MessageSquare className="h-3.5 w-3.5" /> {task.note ? t.editNote : t.addNote}
            </button>
            {!task.done && !task.skipped && (
              <button onClick={() => onSkip(task)} data-testid={`work-skip-${task.id}`}
                className="inline-flex items-center gap-1 rounded-full border border-sand-300 px-3 py-1.5 text-[12px] font-semibold text-ink-700 active:bg-sand-100">
                <Ban className="h-3.5 w-3.5" /> {t.cantBtn}
              </button>
            )}
            {task.skipped && (
              <button onClick={() => onSkip(task)} data-testid={`work-skip-${task.id}`}
                className="text-[12px] font-medium text-ink/45 underline underline-offset-2">{t.changeReason}</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default function WorkPage() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [vid, setVid] = useState("");
  const [view, setView] = useState("today");
  const [area, setArea] = useState("");
  const [busy, setBusy] = useState(false);
  const [whoOpen, setWhoOpen] = useState(false);
  const [other, setOther] = useState("");
  const [skipFor, setSkipFor] = useState(null);
  const [skipText, setSkipText] = useState("");
  const [noteFor, setNoteFor] = useState(null);      // one task's comment
  const [noteText, setNoteText] = useState("");
  const [addFor, setAddFor] = useState(null);        // the area they're adding an item to
  const [addText, setAddText] = useState("");
  const [replyText, setReplyText] = useState("");
  const [note, setNote] = useState("");
  const [openVisit, setOpenVisit] = useState(null);   // a past / upcoming visit, opened read-only
  const [openBusy, setOpenBusy] = useState(false);
  const [assignFor, setAssignFor] = useState(null);   // one list, being put on somebody
  const [carryOpen, setCarryOpen] = useState(false);  // "can't finish it today"
  const [carryWhy, setCarryWhy] = useState("");
  const [offOpen, setOffOpen] = useState(false);      // "not your list anymore?"
  const [uploading, setUploading] = useState("");     // "2/5" while a batch of photos goes up
  const fileRef = useRef(null);
  const photoFor = useRef("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/work/${token}`);
      setD(data);
      setVid((cur) => cur || (data.today[0]?.id ?? ""));
      setNote((cur) => cur || data.today[0]?.note || "");
    } catch { setD({ error: true }); }
  }, [token]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const back = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", back);
    return () => document.removeEventListener("visibilitychange", back);
  }, [load]);

  if (!d) return <div className="grid min-h-screen place-items-center bg-sand-50"><Loader2 className="h-6 w-6 animate-spin text-ink/25" /></div>;
  const t = S[d.contact?.language === "es" ? "es" : "en"];
  if (d.error) return (
    <div className="grid min-h-screen place-items-center bg-sand-50 px-6 text-center">
      <p className="text-[15px] text-ink/55" data-testid="work-bad-link">{S.en.badLink}</p>
    </div>
  );

  const visit = d.today.find((v) => v.id === vid) || null;
  const put = (updated) => setD((p) => ({ ...p, today: p.today.map((v) => (v.id === updated.id ? updated : v)) }));

  // `quiet` = a background save (the note). It must never disable Submit mid-tap.
  const call = async (path, body, quiet = false) => {
    if (!quiet) setBusy(true);
    try {
      const { data } = await api.post(`/work/${token}/visit/${vid}${path}`, body || {});
      if (data.visit) put(data.visit);
      return data;
    } catch (e) {
      if (!quiet) {
        toast.error(errMsg(e, "Try again"));
        setSkipFor(null); setNoteFor(null); setAddFor(null);
        await load();                       // our copy was stale — show them where they really are
      }
      return null;
    } finally { if (!quiet) setBusy(false); }
  };

  const onPhoto = (task) => { photoFor.current = task.id; setTimeout(() => fileRef.current?.click(), 0); };

  // The handoff. A list belongs to the company: the office says who is doing each one, and
  // whoever holds a list can hand it back without anybody at the community lifting a finger.
  const assign = async (payload) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/work/${token}/assign`, payload);
      setAssignFor(null);
      await load();
      toast.success(payload.scope === "visit" && data?.holder ? t.coverDone(data.holder) : t.sent);
    } catch (e) { toast.error(errMsg(e, "Try again")); } finally { setBusy(false); }
  };

  const stepOff = async (payload) => {
    setBusy(true);
    try {
      await api.post(`/work/${token}/step-off`, payload);
      setOffOpen(false);
      await load();
      toast.success(t.offDone);
    } catch (e) { toast.error(errMsg(e, "Try again")); } finally { setBusy(false); }
  };

  // "I ran out of time." The leftovers move to the next day with a reason; today stays unfinished
  // on the record until they're actually done.
  const carry = async () => {
    if (!carryWhy.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/work/${token}/visit/${vid}/carry`, { reason: carryWhy.trim() });
      setCarryOpen(false); setCarryWhy("");
      if (data?.visit) put(data.visit);
      // The next visit just grew by the leftovers, so refresh what "coming up" says it holds.
      await load();
      toast.success(t.carriedTitle(data.count, pretty(data.to_date, d.contact?.language, true)));
      setView("filed");
    } catch (e) { toast.error(errMsg(e, "Try again")); } finally { setBusy(false); }
  };

  // Plain elements, never components defined in render — that remounts every input on each
  // keystroke and types backwards (iter535).
  const handoffSheets = (
    <>
      {assignFor && (
        <AssignSheet t={t} list={assignFor} crew={d.crew || []} me={d.contact?.id} busy={busy}
          onClose={() => setAssignFor(null)} onAssign={assign} />
      )}
      {offOpen && (
        <StepOffSheet t={t} lists={(d.lists || []).filter((l) => l.mine)} busy={busy}
          onClose={() => setOffOpen(false)} onSend={stepOff} />
      )}
      {carryOpen && visit && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 px-4 pb-6" onClick={() => setCarryOpen(false)}>
          <div className="w-full max-w-md rounded-3xl bg-white p-5" onClick={(e) => e.stopPropagation()} data-testid="work-carry-sheet">
            <div className="font-heading text-[16px] font-bold text-ink">{t.carryTitle}</div>
            <p className="mt-1 text-[12.5px] text-ink/55">{t.carrySub(visit.counts.total - visit.counts.done)}</p>
            <div className="mt-3 text-[11px] font-bold uppercase tracking-wide text-ink/40">{t.carryWhy}</div>
            <textarea rows={3} autoFocus value={carryWhy} onChange={(e) => setCarryWhy(e.target.value)}
              placeholder={t.carryWhyHint} data-testid="work-carry-text"
              className="mt-1.5 w-full resize-none rounded-xl border border-sand-300 px-3.5 py-3 text-[14px] outline-none focus:border-redline-400" />
            <PrimaryButton testid="work-carry-send" disabled={busy || !carryWhy.trim()} onClick={carry}>
              {t.carrySend}
            </PrimaryButton>
          </div>
        </div>
      )}
    </>
  );
  const officeCard = d.contact?.office && (d.lists || []).length > 0
    ? <OfficeLists t={t} lists={d.lists} onPick={setAssignFor} />
    : null;
  const offLink = (
    <button onClick={() => setOffOpen(true)} data-testid="work-not-mine"
      className="mx-auto mt-5 block text-[12px] font-medium text-ink/35 underline underline-offset-2">
      {t.notYours}
    </button>
  );
  // Somebody is sick, somebody is double-booked. Whoever holds today's list can put a colleague on
  // it for the day without the office or the manager lifting a finger — tomorrow it is theirs again.
  const myListToday = (d.lists || []).find((l) => l.id === visit?.template_id && (l.mine || d.contact?.office));
  const coverLink = visit && myListToday ? (
    <button onClick={() => setAssignFor({ ...myListToday, visit_id: visit.id })} data-testid="work-cover-today"
      className="mx-auto mt-5 block text-[12.5px] font-semibold text-ink/55 underline underline-offset-2">
      {t.coverToday}
    </button>
  ) : null;


  // Opening a past or upcoming visit from the "coming up · past visits" screen. Read-only:
  // the only thing that can still be written on it is a reply to the office.
  const openOne = async (id) => {
    setOpenBusy(true);
    try {
      const { data } = await api.get(`/work/${token}/visit/${id}`);
      setOpenVisit(data.visit);
      setReplyText("");
      setView("visit");
      scrollPageTop();
    } catch (e) { toast.error(errMsg(e, "Try again")); } finally { setOpenBusy(false); }
  };

  // "Got it" on a note from the office. Anyone holding the link can confirm it; the office needs
  // to know it was read, not just sent.
  const ackNote = async (vid, nid, onDone) => {
    try {
      const { data } = await api.post(`/work/${token}/visit/${vid}/note/${nid}/ack`);
      if (data.visit) onDone(data.visit);
    } catch (e) { toast.error(errMsg(e, "Try again")); }
  };

  const replyOnOpen = async () => {
    const text = replyText.trim();
    if (!text) return;
    setOpenBusy(true);
    try {
      const { data } = await api.post(`/work/${token}/visit/${openVisit.id}/reply`, { text });
      if (data.visit) setOpenVisit({ ...data.visit, scope: openVisit.scope, can_reply: true });
      setReplyText("");
      toast.success(t.sent);
    } catch (e) { toast.error(errMsg(e, "Try again")); } finally { setOpenBusy(false); }
  };
  // Multiple photos per task already stack on the server (`photos` is appended to). What was
  // missing was the picker: one tap, one photo. A crew member documenting a leak takes four.
  const upload = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 12);
    e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    let failed = 0;
    for (let i = 0; i < files.length; i++) {
      if (files.length > 1) setUploading(`${i + 1}/${files.length}`);
      const fd = new FormData();
      fd.append("file", files[i]);
      fd.append("task_id", photoFor.current);
      try {
        const { data } = await api.post(`/work/${token}/visit/${vid}/photo`, fd);
        if (data.visit) put(data.visit);
      } catch { failed += 1; }
    }
    setUploading("");
    setBusy(false);
    if (failed) toast.error(failed === files.length ? t.photoFailed : t.photoSomeFailed(failed));
  };

  const submit = async () => {
    if (note !== (visit?.note || "")) await call("/note", { note }, true);
    const res = await call("/submit");
    if (res?.needs_photo) { toast.error(t.photoFirst(res.needs_photo)); return; }
    if (res?.ok) setView("filed");
  };

  // ── no work today ────────────────────────────────────────────────────────
  if (!visit && !["more", "visit"].includes(view)) return (
    <Shell>
      <Head community={d.vendor} name={t.nothingToday} sub={t.nothingTodaySub} />
      {officeCard}
      <button onClick={() => setView("more")} data-testid="work-more-link"
        className="mt-6 text-[12.5px] font-medium text-ink/40 underline underline-offset-2">{t.comingUp}</button>
      {offLink}
      {handoffSheets}
    </Shell>
  );

  // The three sheets are needed on the area screen AND on the last screen before submitting, which
  // is where "say why" lives. Built once as an ELEMENT — never as a component defined in render,
  // which remounts the textarea on every keystroke and types the note backwards (iter535).
  const sheets = (
    <>
        {uploading && (
          <div data-testid="work-photo-progress"
            className="fixed inset-x-0 bottom-0 z-[60] bg-ink/90 px-5 py-3 text-center text-[13px] font-semibold text-white">
            {t.addingPhotos(uploading)}
          </div>
        )}
        {skipFor && (
          <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 px-4 pb-6" onClick={() => setSkipFor(null)}>
            <div className="w-full max-w-md rounded-3xl bg-white p-5" onClick={(e) => e.stopPropagation()} data-testid="work-skip-sheet">
              <div className="font-heading text-[16px] font-bold text-ink">{t.cantTitle}</div>
              <div className="mt-0.5 text-[12.5px] text-ink/45">{skipFor.label}</div>
              <textarea rows={3} autoFocus value={skipText} onChange={(e) => setSkipText(e.target.value)} placeholder={t.cantPh}
                data-testid="work-skip-text"
                className="mt-3 w-full resize-none rounded-xl border border-sand-300 px-3.5 py-3 text-[14px] outline-none focus:border-redline-400" />
              <PrimaryButton testid="work-skip-send" disabled={busy || !skipText.trim()}
                onClick={async () => { const r = await call("/skip", { task_id: skipFor.id, reason: skipText.trim() }); if (r?.ok) { setSkipFor(null); toast.success(t.sent); } }}>
                {t.send}
              </PrimaryButton>
            </div>
          </div>
        )}

        {noteFor && (
          <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 px-4 pb-6" onClick={() => setNoteFor(null)}>
            <div className="w-full max-w-md rounded-3xl bg-white p-5" onClick={(e) => e.stopPropagation()} data-testid="work-note-sheet">
              <div className="font-heading text-[16px] font-bold text-ink">{t.noteTitle}</div>
              <div className="mt-0.5 text-[12.5px] text-ink/45">{noteFor.label}</div>
              <textarea rows={3} autoFocus value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder={t.taskNotePh}
                data-testid="work-note-text"
                className="mt-3 w-full resize-none rounded-xl border border-sand-300 px-3.5 py-3 text-[14px] outline-none focus:border-redline-400" />
              <PrimaryButton testid="work-note-send" disabled={busy}
                onClick={async () => { const r = await call("/task-note", { task_id: noteFor.id, note: noteText.trim() }); if (r?.ok) { setNoteFor(null); toast.success(t.sent); } }}>
                {t.send}
              </PrimaryButton>
            </div>
          </div>
        )}

        {addFor && (
          <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 px-4 pb-6" onClick={() => setAddFor(null)}>
            <div className="w-full max-w-md rounded-3xl bg-white p-5" onClick={(e) => e.stopPropagation()} data-testid="work-add-sheet">
              <div className="font-heading text-[16px] font-bold text-ink">{t.addTitle}</div>
              <div className="mt-0.5 text-[12.5px] text-ink/45">{addFor}</div>
              <input autoFocus value={addText} onChange={(e) => setAddText(e.target.value)} placeholder={t.addPh}
                data-testid="work-add-text"
                className="mt-3 w-full rounded-xl border border-sand-300 px-3.5 py-3 text-[14px] outline-none focus:border-redline-400" />
              <p className="mt-3 text-[12px] text-ink/45">{t.addKeepHint}</p>
              <div className="mt-2 flex gap-2">
                <button data-testid="work-add-today" disabled={busy || !addText.trim()}
                  onClick={async () => { const r = await call("/add-task", { label: addText.trim(), section: addFor, keep: false }); if (r?.ok) { setAddFor(null); toast.success(t.sent); } }}
                  className="flex-1 rounded-2xl border border-sand-300 px-4 py-3 text-[14px] font-semibold text-ink disabled:opacity-40">
                  {t.justToday}
                </button>
                <button data-testid="work-add-always" disabled={busy || !addText.trim()}
                  onClick={async () => { const r = await call("/add-task", { label: addText.trim(), section: addFor, keep: true }); if (r?.ok) { setAddFor(null); toast.success(t.keptOnList); } }}
                  className="flex-1 rounded-2xl bg-ink px-4 py-3 text-[14px] font-semibold text-white disabled:opacity-40">
                  {t.everyTime}
                </button>
              </div>
            </div>
          </div>
        )}
    </>
  );
  if (view === "more") return (
    <Shell>
      <BackLink label={t.today} onClick={() => setView("today")} />
      <Head community={d.vendor} name={t.comingUp} />
      <div className="mt-5 space-y-2" data-testid="work-upcoming">
        {!d.upcoming.length && <p className="text-[13px] text-ink/40" data-testid="work-upcoming-empty">{t.noUpcoming}</p>}
        {d.upcoming.map((u, i) => (
          <button key={u.id || i} onClick={() => u.id && openOne(u.id)} disabled={openBusy || !u.id}
            data-testid={`work-upcoming-${u.id || i}`}
            className="flex w-full items-center gap-3 rounded-2xl border border-sand-200 bg-white px-4 py-3.5 text-left transition-colors active:bg-sand-100">
            <span className="min-w-0 flex-1">
              <span className="block text-[14.5px] font-semibold text-ink">{u.name}</span>
              <span className="block text-[12px] text-ink/45">{pretty(u.date, d.contact?.language, true)} · {u.community} · {u.tasks} {t.tasks}</span>
            </span>
            <ChevronRight className="h-4 w-4 shrink-0 text-sand-400" />
          </button>
        ))}
      </div>
      <div className="mt-6 text-[10.5px] font-bold uppercase tracking-[0.12em] text-ink/35">{t.pastVisits}</div>
      <div className="mt-2 space-y-2" data-testid="work-history">
        {!d.history.length && <p className="text-[13px] text-ink/40" data-testid="work-history-empty">{t.noHistory}</p>}
        {d.history.map((h) => (
          <button key={h.id} onClick={() => openOne(h.id)} disabled={openBusy} data-testid={`work-history-${h.id}`}
            className="flex w-full items-center gap-3 rounded-2xl border border-sand-200 bg-white px-4 py-3.5 text-left transition-colors active:bg-sand-100">
            <span className="min-w-0 flex-1">
              <span className="block text-[14.5px] font-semibold text-ink">{h.name}</span>
              <span className="block text-[12px] text-ink/45">
                {pretty(h.date, d.contact?.language, true)} · {h.status === "missed" ? t.missed : `${h.counts.done} ${t.of} ${h.counts.total} ${t.done}`}
                {h.counts.photos ? ` · ${h.counts.photos} ${t.photos.toLowerCase()}` : ""}
              </span>
            </span>
            <ChevronRight className="h-4 w-4 shrink-0 text-sand-400" />
          </button>
        ))}
      </div>
      {officeCard}
      {offLink}
      {handoffSheets}
    </Shell>
  );

  // ── one past / upcoming visit, read only ─────────────────────────────────
  if (view === "visit" && openVisit) {
    const v = openVisit;
    const upcoming = v.scope === "upcoming";
    const win = v.window?.start ? `${v.window.start}${v.window.end ? ` – ${v.window.end}` : ""}` : "";
    const msgs = (v.thread || []).filter((e) => !["system", "note"].includes(e.kind));
    return (
      <Shell>
        <BackLink label={t.comingUp} onClick={() => { setOpenVisit(null); setView("more"); }} />
        <Head community={v.community} name={v.name}
          sub={[pretty(v.date, d.contact?.language), win, t[STATUS_WORD[v.status]] || ""].filter(Boolean).join(" · ")} />
        {upcoming && <p className="mt-3 text-[12.5px] leading-relaxed text-ink/45" data-testid="work-visit-preview-note">{t.previewNote}</p>}
        <VisitRecap t={t} visit={v} upcoming={upcoming} />
        <PinnedNotes notes={v.pinned} t={t}
          onAck={(nid) => ackNote(v.id, nid, (fresh) => setOpenVisit({ ...fresh, scope: v.scope, can_reply: v.can_reply }))} />

        <div className="mt-6 text-[10.5px] font-bold uppercase tracking-[0.12em] text-ink/35">{t.withOffice}</div>
        <div className="mt-2 space-y-2.5" data-testid="work-visit-thread">
          {!msgs.length && <p className="text-[13px] text-ink/40" data-testid="work-visit-thread-empty">{t.noMessages}</p>}
          {msgs.map((e) => <ThreadMsg key={e.id} e={e} t={t} tasks={v.tasks} />)}
        </div>
        {v.can_reply && (
          <div className="mt-4 flex items-end gap-2">
            <textarea rows={2} value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder={t.reply}
              data-testid="work-visit-reply-input"
              className="min-h-[50px] flex-1 resize-none rounded-xl border border-sand-300 bg-white px-3.5 py-3 text-[14px] outline-none placeholder:text-ink/30 focus:border-redline-400" />
            <button data-testid="work-visit-reply-send" disabled={openBusy || !replyText.trim()} onClick={replyOnOpen}
              className="grid h-[50px] w-[50px] place-items-center rounded-xl bg-redline-500 text-white disabled:opacity-40">
              <Send className="h-4 w-4" />
            </button>
          </div>
        )}
      </Shell>
    );
  }

  // ── the conversation with the office ─────────────────────────────────────
  if (view === "thread") return (
    <Shell>
      <BackLink label={t.today} onClick={() => setView("today")} />
      <Head community={visit.community} name={t.office} sub={visit.name} />
      <div className="mt-5 space-y-2.5" data-testid="work-thread">
        {visit.thread.filter((e) => !["system", "note"].includes(e.kind)).map((e) => <ThreadMsg key={e.id} e={e} t={t} tasks={visit.tasks} />)}
      </div>
      <div className="mt-4 flex items-end gap-2">
        <textarea rows={2} value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder={t.reply}
          data-testid="work-reply-input"
          className="min-h-[50px] flex-1 resize-none rounded-xl border border-sand-300 bg-white px-3.5 py-3 text-[14px] outline-none placeholder:text-ink/30 focus:border-redline-400" />
        <button data-testid="work-reply-send" disabled={busy || !replyText.trim()}
          onClick={async () => { const r = await call("/reply", { text: replyText.trim() }); if (r?.ok) { setReplyText(""); toast.success(t.sent); } }}
          className="grid h-[50px] w-[50px] place-items-center rounded-xl bg-redline-500 text-white disabled:opacity-40">
          <Send className="h-4 w-4" />
        </button>
      </div>
    </Shell>
  );

  // ── filed ────────────────────────────────────────────────────────────────
  if (view === "filed" || ["submitted", "vendor_replied", "approved", "carried"].includes(visit.status)) {
    return (
      <Shell>
        <Head community={visit.community} name={visit.name} sub={pretty(visit.date, d.contact?.language)} />
        {visit.carried && (
          <div className="mt-5 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3.5" data-testid="work-carried-out">
            <div className="text-[10.5px] font-bold uppercase tracking-wide text-amber-800">{t.carried}</div>
            <p className="mt-1 text-[14px] font-semibold leading-snug text-ink">
              {t.carriedTitle(visit.carried.count, pretty(visit.carried.to_date, d.contact?.language, true))}
            </p>
            {visit.carried.reason && <p className="mt-1 text-[13px] text-ink/60">“{visit.carried.reason}”</p>}
          </div>
        )}
        <Filed t={t} visit={visit} onThread={() => setView("thread")} questionLabel={questionLabel(visit, t)}
          next={d.upcoming[0] ? { ...d.upcoming[0], date: pretty(d.upcoming[0].date, d.contact?.language, true) } : null} />
        <button onClick={() => setView("more")} data-testid="work-more-link"
          className="mx-auto mt-6 block text-[12.5px] font-medium text-ink/40 underline underline-offset-2">{t.comingUp}</button>
        {officeCard}
        {offLink}
        {handoffSheets}
      </Shell>
    );
  }

  // ── before you go ────────────────────────────────────────────────────────
  if (view === "review") {
    const undone = visit.tasks.filter((x) => !x.done);
    return (
      <Shell>
        <BackLink label={t.today} onClick={() => setView("today")} />
        <Head community={visit.community} name={t.beforeYouGo} sub={`${visit.counts.done} ${t.of} ${visit.counts.total} ${t.done}`} />
        <Card className="mt-5">
          <div className="text-[10.5px] font-bold uppercase tracking-[0.1em] text-ink/35">{t.note}</div>
          <textarea rows={4} value={note} data-testid="work-note"
            onChange={(e) => setNote(e.target.value)} onBlur={() => call("/note", { note }, true)}
            placeholder={t.notePh}
            className="mt-2 w-full resize-none bg-transparent text-[14px] leading-relaxed text-ink outline-none placeholder:text-ink/30" />
        </Card>
        {undone.length > 0 && (
          <Card className="mt-3" data-testid="work-undone">
            <div className="text-[10.5px] font-bold uppercase tracking-[0.1em] text-ink/35">{t.notDoneTitle}</div>
            <p className="mt-1 text-[12px] leading-relaxed text-ink/45">{t.notDoneHint}</p>
            {undone.map((x) => (
              <div key={x.id} className="mt-2.5 border-t border-sand-100 pt-2.5" data-testid={`work-undone-${x.id}`}>
                <div className="text-[13.5px] font-semibold text-ink">{x.label}</div>
                {x.skipped
                  ? <div className="mt-0.5 text-[12px] text-ink/50">{t.told}: {x.reason}</div>
                  : <div className="mt-0.5 text-[12px] text-amber-800">{t.noReason}</div>}
                <button onClick={() => { setSkipFor(x); setSkipText(x.reason || ""); }}
                  data-testid={`work-undone-why-${x.id}`}
                  className="mt-1.5 rounded-full border border-sand-300 px-3 py-1.5 text-[12.5px] font-semibold text-ink active:bg-sand-100">
                  {x.skipped ? t.changeReason : t.sayWhy}
                </button>
              </div>
            ))}
          </Card>
        )}
        <PrimaryButton tone="green" testid="work-submit" disabled={busy || !!visit.counts.missing_photos} onClick={submit}>
          {visit.counts.missing_photos ? t.photoFirst(visit.counts.missing_photos) : t.submit}
        </PrimaryButton>
        <p className="mt-2.5 text-center text-[11.5px] text-ink/40">{t.saved}</p>
        <input ref={fileRef} type="file" accept="image/*" multiple onChange={upload} className="hidden" />
        {sheets}
      </Shell>
    );
  }

  // ── inside one area ──────────────────────────────────────────────────────
  if (view === "area") {
    const list = visit.tasks.filter((x) => x.section === area);
    const sec = visit.sections.find((s) => s.name === area) || { done: 0, total: 0 };
    const idx = visit.sections.findIndex((s) => s.name === area);
    const next = visit.sections[idx + 1];
    return (
      <Shell>
        <BackLink label={t.today} onClick={() => setView("today")} />
        <Head community={visit.community} name={area} sub={`${sec.done} ${t.of} ${sec.total} ${t.done}`} />
        <Progress done={sec.done} total={sec.total} />
        <div className="mt-5">
          {list.map((task) => (
            <TaskCard key={task.id} t={t} task={task} busy={busy} lang={d.contact?.language}
              onTick={(x) => call("/tick", { task_id: x.id, done: !x.done })}
              onPhoto={onPhoto}
              onStep={(x, s) => call("/step", { task_id: x.id, step_id: s.id, done: !s.done })}
              onNote={(x) => { setNoteFor(x); setNoteText(x.note || ""); }}
              onSkip={(x) => { setSkipFor(x); setSkipText(""); }} />
          ))}
          {visit.can_add && (
            <button onClick={() => { setAddFor(area); setAddText(""); }} data-testid="work-add-item"
              className="mt-1 flex w-full items-center justify-center gap-1.5 rounded-2xl border border-dashed border-sand-300 px-4 py-3 text-[13px] font-semibold text-ink/45 active:bg-sand-100">
              <Plus className="h-4 w-4" /> {t.addItem}
            </button>
          )}
        </div>
        <PrimaryButton testid="work-next-area"
          onClick={() => { if (next) { setArea(next.name); scrollPageTop(); } else setView("review"); }}>
          {next ? `${t.nextArea} · ${next.name}` : t.beforeYouGo}
        </PrimaryButton>
        <input ref={fileRef} type="file" accept="image/*" multiple onChange={upload} className="hidden" data-testid="work-file-input" />

        {sheets}
      </Shell>
    );
  }

  // ── today ────────────────────────────────────────────────────────────────
  const who = visit.tech_name || d.contact?.name || "";
  return (
    <Shell>
      <Head community={visit.community} name={visit.name}
        sub={`${pretty(visit.date, d.contact?.language)} · ${visit.counts.total} ${t.tasks}`} />

      {visit.open_question && (
        <QuestionLine label={questionLabel(visit, t)} text={visit.open_question} hint={t.tapToReply} onClick={() => setView("thread")} />
      )}

      <PinnedNotes notes={visit.pinned} t={t} onAck={(nid) => call(`/note/${nid}/ack`)} />

      <Card className="mt-4" data-testid="work-who">
        <button onClick={() => setWhoOpen((v) => !v)} className="flex w-full items-center gap-2.5 text-left">
          <Check className="h-4 w-4 shrink-0 text-emerald-700" strokeWidth={3} />
          <span className="min-w-0 flex-1">
            <span className="block text-[10.5px] font-bold uppercase tracking-wide text-ink/35">{t.whoHere}</span>
            <span className="block text-[15px] font-semibold text-ink">{who}</span>
          </span>
          <span className="shrink-0 text-[11.5px] text-ink/35">{t.tapChange}</span>
        </button>
        {whoOpen && (
          <div className="mt-3 space-y-1.5 border-t border-sand-100 pt-3" data-testid="work-who-options">
            {(d.crew || []).map((c) => (
              <button key={c.id} data-testid={`work-who-${c.id}`}
                onClick={async () => { await call("/who", { name: c.name }); setWhoOpen(false); }}
                className={`flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-left text-[14px] font-semibold ${
                  c.name === who ? "bg-emerald-50 text-emerald-800" : "bg-sand-50 text-ink"}`}>
                {c.name}
              </button>
            ))}
            <div className="flex gap-2 pt-1">
              <input value={other} onChange={(e) => setOther(e.target.value)} placeholder={t.typeName} data-testid="work-who-other"
                className="min-w-0 flex-1 rounded-xl border border-sand-300 px-3 py-2.5 text-[14px] outline-none focus:border-redline-400" />
              <button data-testid="work-who-save" disabled={!other.trim() || busy}
                onClick={async () => { await call("/who", { name: other.trim() }); setOther(""); setWhoOpen(false); }}
                className="rounded-xl bg-ink px-4 text-[13px] font-semibold text-white disabled:opacity-40">{t.send}</button>
            </div>
          </div>
        )}
      </Card>

      {officeCard}

      {d.today.length > 1 && (
        <div className="mt-4 space-y-2" data-testid="work-other-jobs">
          {d.today.filter((v) => v.id !== vid).map((v) => (
            <button key={v.id} onClick={() => { setVid(v.id); setView("today"); }} data-testid={`work-job-${v.id}`}
              className="flex w-full items-center gap-2 rounded-2xl border border-sand-200 bg-white px-4 py-3 text-left">
              <span className="min-w-0 flex-1">
                <span className="block text-[14px] font-semibold text-ink">{v.name}</span>
                <span className="block text-[11.5px] text-ink/45">{v.community} · {v.counts.done} {t.of} {v.counts.total}</span>
              </span>
              <ChevronRight className="h-4 w-4 shrink-0 text-sand-400" />
            </button>
          ))}
        </div>
      )}

      <div className="mt-5 text-[10.5px] font-bold uppercase tracking-[0.12em] text-ink/35">{t.today}</div>
      <div className="mt-2 space-y-2.5" data-testid="work-areas">
        {visit.sections.map((s) => (
          <AreaRow key={s.name} area={s} ofWord={t.of} onClick={() => { setArea(s.name); setView("area"); scrollPageTop(); }} />
        ))}
      </div>

      {visit.counts.done > 0 && (
        <PrimaryButton tone="green" testid="work-go-review" onClick={() => setView("review")}>
          {t.beforeYouGo}
        </PrimaryButton>
      )}

      <button onClick={() => setView("more")} data-testid="work-more-link"
        className="mx-auto mt-6 block text-[12.5px] font-medium text-ink/40 underline underline-offset-2">{t.comingUp}</button>
      {visit.counts.done < visit.counts.total && (
        <button onClick={() => setCarryOpen(true)} data-testid="work-carry-link"
          className="mx-auto mt-5 block text-[12.5px] font-semibold text-ink/55 underline underline-offset-2">
          {t.carryLink}
        </button>
      )}
      {coverLink}
      {offLink}
      {handoffSheets}
      <div className="mt-6 flex items-center justify-center gap-1.5 text-[11px] text-ink/25">
        <Camera className="h-3 w-3" /> {d.vendor}
      </div>
    </Shell>
  );
}
